import { useTranslation } from "react-i18next";

/** 4-point sparkle glyph (matches the design reference exactly). */
const Sparkle = ({
  size = 24,
  className,
}: {
  size?: number;
  className?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M12 2c.5 4.5 2.5 6.5 7 7-4.5.5-6.5 2.5-7 7-.5-4.5-2.5-6.5-7-7 4.5-.5 6.5-2.5 7-7z"
      fill="currentColor"
    />
  </svg>
);

/** Clock glyph for the "Time saved" pill. */
const Clock = ({ size = 13 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);

const partyCard =
  "bg-white/85 border border-homeowners-purple/15 rounded-[10px] font-montserrat font-semibold text-homeowners-headline shadow-[0_6px_16px_-10px_rgba(30,29,53,0.4)]";

export const AIConnectorDiagram = () => {
  const [t] = useTranslation("homeowners");

  const parties = [
    t("Diagram.PropertyManagers"),
    t("Diagram.HOAs"),
    t("Diagram.Vendors"),
    t("Diagram.Contractors"),
  ];

  return (
    <>
      {/* ===== Desktop diagram (xl+) ===== */}
      <div
        className="hidden xl:block relative w-[560px] h-[400px]"
        aria-hidden="true"
      >
        <svg
          width="560"
          height="400"
          viewBox="0 0 560 400"
          fill="none"
          className="absolute inset-0"
        >
          <path
            d="M 175 70 C 240 70, 250 190, 254 190"
            stroke="rgba(54,50,84,0.32)"
            strokeWidth="2"
            strokeDasharray="4 6"
            className="animate-[dashFlow_2.4s_linear_infinite]"
          />
          <path
            d="M 175 150 C 240 150, 250 190, 254 190"
            stroke="rgba(54,50,84,0.32)"
            strokeWidth="2"
            strokeDasharray="4 6"
            className="animate-[dashFlow_2.65s_linear_infinite]"
          />
          <path
            d="M 175 230 C 240 230, 250 190, 254 190"
            stroke="rgba(54,50,84,0.32)"
            strokeWidth="2"
            strokeDasharray="4 6"
            className="animate-[dashFlow_2.9s_linear_infinite]"
          />
          <path
            d="M 175 310 C 240 310, 250 190, 254 190"
            stroke="rgba(54,50,84,0.32)"
            strokeWidth="2"
            strokeDasharray="4 6"
            className="animate-[dashFlow_3.15s_linear_infinite]"
          />
          <path
            d="M 346 190 C 400 190, 420 190, 470 190"
            stroke="rgba(138,153,142,0.7)"
            strokeWidth="2.5"
            strokeDasharray="5 6"
            className="animate-[dashFlow_1.8s_linear_infinite]"
          />
        </svg>

        {/* Party nodes */}
        <div className={`absolute left-0 top-[53px] px-3.5 py-2 text-[13px] whitespace-nowrap ${partyCard}`}>
          {parties[0]}
        </div>
        <div className={`absolute left-0 top-[133px] px-3.5 py-2 text-[13px] whitespace-nowrap ${partyCard}`}>
          {parties[1]}
        </div>
        <div className={`absolute left-0 top-[213px] px-3.5 py-2 text-[13px] whitespace-nowrap ${partyCard}`}>
          {parties[2]}
        </div>
        <div className={`absolute left-0 top-[293px] px-3.5 py-2 text-[13px] whitespace-nowrap ${partyCard}`}>
          {parties[3]}
        </div>

        {/* AI hub */}
        <div className="absolute left-[248px] top-[138px] w-[104px] h-[104px]">
          <span className="absolute inset-0 rounded-full border-2 border-homeowners-green/60 animate-[ringExpand_2.6s_ease-out_infinite]" />
          <span className="absolute inset-0 rounded-full border-2 border-homeowners-purple/40 animate-[ringExpand_2.6s_ease-out_infinite] [animation-delay:1.3s]" />
          <div className="absolute inset-[14px] rounded-full bg-[linear-gradient(135deg,#363254,#4a456c)] flex flex-col items-center justify-center text-white shadow-[0_16px_40px_-12px_rgba(54,50,84,0.7)]">
            <Sparkle size={26} className="animate-[sparkSpin_3s_ease-in-out_infinite]" />
            <b className="font-montserrat font-bold text-[11px] tracking-[0.14em] mt-[3px]">
              {t("Diagram.AI")}
            </b>
          </div>
          {/* "Time saved" pill — static centering wrapper + inner bob so it
              stays centered under prefers-reduced-motion */}
          <div className="absolute -top-[22px] left-1/2 -translate-x-1/2">
            <div className="inline-flex items-center gap-[5px] whitespace-nowrap py-[5px] pr-[11px] pl-[9px] rounded-full bg-white border border-homeowners-purple/15 shadow-[0_8px_20px_-8px_rgba(30,29,53,0.5)] animate-[aiFloat_4s_ease-in-out_infinite]">
              <span className="text-homeowners-purple">
                <Clock size={13} />
              </span>
              <b className="font-montserrat font-bold text-[11px] text-homeowners-headline">
                {t("Diagram.TimeSaved")}
              </b>
            </div>
          </div>
        </div>

        {/* Homeowners destination */}
        <div className="absolute left-[436px] top-[156px] w-[124px] h-[68px]">
          <div className="absolute inset-[-14px] rounded-[18px] bg-[radial-gradient(circle,rgba(138,153,142,0.55),transparent_70%)] blur-[8px] animate-[aiGlow_3s_ease-in-out_infinite]" />
          <span className="absolute inset-0 rounded-[14px] border-2 border-homeowners-green/70 animate-[ringExpand_2.6s_ease-out_infinite]" />
          <div className="absolute inset-0 px-[22px] bg-[linear-gradient(135deg,#8a998e,#74857a)] rounded-[14px] text-white flex items-center justify-center text-center font-montserrat font-extrabold text-[14px] shadow-[0_12px_30px_-8px_rgba(138,153,142,0.9),inset_0_0_0_1.5px_rgba(255,255,255,0.25)] animate-[aiFloat_4.5s_ease-in-out_infinite]">
            {t("Diagram.Homeowners")}
          </div>
        </div>
      </div>

      {/* ===== Mobile / tablet diagram (below xl) ===== */}
      <div
        className="flex xl:hidden flex-col items-center w-full max-w-[420px] mx-auto mt-2"
        aria-hidden="true"
      >
        <div className="grid grid-cols-2 gap-2.5 w-full">
          {parties.map((label) => (
            <span
              key={label}
              className={`text-center py-2.5 px-2 text-[12px] ${partyCard}`}
            >
              {label}
            </span>
          ))}
        </div>

        <div className="w-[2px] h-[30px] my-1.5 bg-[linear-gradient(rgba(54,50,84,0.45)_60%,transparent_0)] bg-[length:2px_10px] animate-[flowDown_1.6s_linear_infinite]" />

        <div className="relative w-[96px] h-[96px]">
          <span className="absolute inset-0 rounded-full border-2 border-homeowners-green/60 animate-[ringExpand_2.6s_ease-out_infinite]" />
          <div className="absolute inset-[12px] rounded-full bg-[linear-gradient(135deg,#363254,#4a456c)] flex flex-col items-center justify-center text-white shadow-[0_16px_40px_-12px_rgba(54,50,84,0.7)]">
            <Sparkle size={24} className="animate-[sparkSpin_3s_ease-in-out_infinite]" />
            <b className="font-montserrat font-bold text-[10px] tracking-[0.14em] mt-[2px]">
              {t("Diagram.AI")}
            </b>
          </div>
          <div className="absolute -top-[18px] left-1/2 -translate-x-1/2 inline-flex items-center gap-[5px] whitespace-nowrap py-1 pr-2.5 pl-2 rounded-full bg-white border border-homeowners-purple/15 shadow-[0_8px_20px_-8px_rgba(30,29,53,0.5)]">
            <span className="text-homeowners-purple">
              <Clock size={12} />
            </span>
            <b className="font-montserrat font-bold text-[10px] text-homeowners-headline">
              {t("Diagram.TimeSaved")}
            </b>
          </div>
        </div>

        <div className="w-[2px] h-[30px] my-1.5 bg-[linear-gradient(rgba(138,153,142,0.8)_60%,transparent_0)] bg-[length:2px_10px] animate-[flowDown_1.6s_linear_infinite]" />

        <div className="relative px-[30px] py-3.5 rounded-[14px] bg-[linear-gradient(135deg,#8a998e,#74857a)] text-white font-montserrat font-extrabold text-[14px] shadow-[0_12px_30px_-8px_rgba(138,153,142,0.9),inset_0_0_0_1.5px_rgba(255,255,255,0.25)]">
          <div className="absolute inset-[-12px] -z-10 rounded-[18px] bg-[radial-gradient(circle,rgba(138,153,142,0.5),transparent_70%)] blur-[8px] animate-[aiGlow_3s_ease-in-out_infinite]" />
          {t("Diagram.Homeowners")}
        </div>
      </div>
    </>
  );
};
